// Radar-style pulsing dot: a solid core with expanding rings rippling out of it.
//
// The design (DashboardRevampContent.dc.html) animates its live dots with
//   @keyframes ccPulse { 0%,100% {opacity:1;transform:scale(1)}
//                        50%     {opacity:.3;transform:scale(1.35)} }
// i.e. the dot itself throbs. This keeps that core throb but adds outward rings
// so it reads as a radar ping rather than a blinking dot — the banner's
// "CLASS TODAY" / LIVE markers previously rendered a completely static circle.

import React, {useEffect, useRef} from 'react';
import {Animated, Easing, StyleSheet, View} from 'react-native';

interface RadarDotProps {
  color?: string;
  /** Diameter of the solid core. Rings scale off this. */
  size?: number;
  /** One full ping cycle, ms. */
  duration?: number;
}

export default function RadarDot({
  color = '#FACF47',
  size = 7,
  duration = 1800,
}: RadarDotProps) {
  // Two rings offset by half a cycle so there's always one mid-flight.
  const ringA = useRef(new Animated.Value(0)).current;
  const ringB = useRef(new Animated.Value(0)).current;
  const core = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const ping = (v: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(v, {
            toValue: 1,
            duration,
            easing: Easing.out(Easing.quad),
            useNativeDriver: true,
          }),
          Animated.timing(v, {
            toValue: 0,
            duration: 0,
            useNativeDriver: true,
          }),
        ]),
      );

    // The design's own core throb.
    const throb = Animated.loop(
      Animated.sequence([
        Animated.timing(core, {
          toValue: 1,
          duration: duration / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(core, {
          toValue: 0,
          duration: duration / 2,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    );

    const a = ping(ringA, 0);
    const b = ping(ringB, duration / 2);
    a.start();
    b.start();
    throb.start();
    return () => {
      a.stop();
      b.stop();
      throb.stop();
    };
  }, [ringA, ringB, core, duration]);

  const ringStyle = (v: Animated.Value) => ({
    transform: [
      {scale: v.interpolate({inputRange: [0, 1], outputRange: [1, 3.2]})},
    ],
    opacity: v.interpolate({inputRange: [0, 1], outputRange: [0.55, 0]}),
  });

  return (
    <View style={[styles.wrap, {width: size * 3.4, height: size * 3.4}]}>
      <Animated.View
        style={[
          styles.ring,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
          },
          ringStyle(ringA),
        ]}
      />
      <Animated.View
        style={[
          styles.ring,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
          },
          ringStyle(ringB),
        ]}
      />
      <Animated.View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color,
          transform: [
            {
              scale: core.interpolate({
                inputRange: [0, 1],
                outputRange: [1, 1.35],
              }),
            },
          ],
          opacity: core.interpolate({
            inputRange: [0, 1],
            outputRange: [1, 0.3],
          }),
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {alignItems: 'center', justifyContent: 'center', flexShrink: 0},
  ring: {position: 'absolute'},
});
